import { useAppDispatch } from '@/state/hooks'
import React, { FC, useEffect } from 'react'
import {
  setSession,
  setUserAuthSuccess,
  setUserAuthEventPasswordRecovery
} from '@/state/userAuth/reducer'
import { supabaseClient } from '@/configs/supabase'
import { useNotifications } from '@/hooks/useNotifications'
import { setNotifications } from '@/state/notifications/reducer'
import {
  useUserKnowledgeTest,
  useUserSuitabilityTest,
  useUserVerificationDetails,
  useUserKyc
} from '@/hooks/userVerificationDetails'
import {
  clearUserKnowledgeTest,
  clearUserKyc,
  clearUserSuitabilityTest,
  clearUserVerificationDetails,
  setUserKnowledgeTest,
  setUserKnowledgeTestSubLifeCycleStatus,
  setUserKyc,
  setUserKycSubLifeCycleStatus,
  setUserSuitabilityTest,
  setUserSuitabilityTestSubLifeCycleStatus,
  setUserVerificationDetails,
  setUserVerificationDetailsSubLifeCycleStatus
} from '@/state/userVerificationDetails/reducer'
import { useToastApplication } from '@/state/application/hooks'
import moment from 'moment'
import { useUserProfile } from '@/hooks/useUserProfile'
import { setAvatarUrl, setDisplayName } from '@/state/userProfile/reducer'

const SupabaseUpdater: FC = () => {
  const dispatch = useAppDispatch()
  const userProfile = useUserProfile()
  const notifications = useNotifications(5)
  const userVerificationDetails = useUserVerificationDetails()
  const { fail } = useToastApplication()

  const userSuitabilityTest = useUserSuitabilityTest(
    userVerificationDetails?.data?.current_suitability_test_id
  )

  const userKnowledgeTest = useUserKnowledgeTest(
    userVerificationDetails?.data?.current_knowledge_test_id
  )

  const userKyc = useUserKyc(userVerificationDetails?.data?.current_kyc_id)

  useEffect(() => {
    supabaseClient.auth.getSession().then(({ data: { session } }) => {
      dispatch(setSession(session))
      if (session !== null) {
        dispatch(setUserAuthSuccess(true))
      }
    })

    const {
      data: { subscription }
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      dispatch(setSession(session))
      if (session !== null) {
        dispatch(setUserAuthSuccess(true))
      }

      if (_event === 'PASSWORD_RECOVERY') {
        dispatch(setUserAuthEventPasswordRecovery(true))
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // ? Manage UserProfile
  useEffect(() => {
    if (userProfile?.display_name) {
      dispatch(setDisplayName(userProfile?.display_name))
    }
    if (userProfile?.avatar_url) {
      dispatch(setAvatarUrl(userProfile?.avatar_url))
    }
  }, [userProfile])

  // ? Manage Notifications
  useEffect(() => {
    if (Array.isArray(notifications)) {
      const mapNotificationsData = notifications.map((noti) => {
        let modifiedData = noti.data
        if ('data' in noti) {
          modifiedData =
            typeof noti.data === 'string' ? JSON.parse(noti.data) : noti.data
        }
        // TODO: hotfix feature toast error when id card is duplicate
        if (noti.reason === 'USER_KYC_ID_CARD_DUPLICATE') {
          if (moment().utc().diff(moment.utc(noti.created_at), 'second') < 10) {
            // @ts-ignore
            fail({ message: modifiedData?.errorMessage })
          }
        }
        return { ...noti, data: modifiedData }
      })

      dispatch(setNotifications(mapNotificationsData))
    }
  }, [notifications])

  // ? Manage User verifications
  useEffect(() => {
    dispatch(
      setUserVerificationDetailsSubLifeCycleStatus(
        userVerificationDetails.subLifeCycleStatus
      )
    )
    if (
      userVerificationDetails?.data &&
      userVerificationDetails.subLifeCycleStatus === 'live'
    ) {
      dispatch(setUserVerificationDetails(userVerificationDetails.data))
    } else if (userVerificationDetails.data === undefined) {
      dispatch(clearUserVerificationDetails())
    }
  }, [userVerificationDetails])

  // ? Manage User Suitability Test
  useEffect(() => {
    dispatch(
      setUserSuitabilityTestSubLifeCycleStatus(
        userSuitabilityTest.subLifeCycleStatus
      )
    )
    if (
      userSuitabilityTest?.data &&
      userSuitabilityTest.subLifeCycleStatus === 'live'
    ) {
      dispatch(setUserSuitabilityTest(userSuitabilityTest.data))
    } else if (userVerificationDetails.data === undefined) {
      dispatch(clearUserSuitabilityTest())
    }
  }, [userSuitabilityTest])

  // ? Manage User Knowledge test
  useEffect(() => {
    dispatch(
      setUserKnowledgeTestSubLifeCycleStatus(
        userKnowledgeTest.subLifeCycleStatus
      )
    )
    if (
      userKnowledgeTest?.data &&
      userKnowledgeTest.subLifeCycleStatus === 'live'
    ) {
      dispatch(setUserKnowledgeTest(userKnowledgeTest.data))
    } else if (userVerificationDetails.data === undefined) {
      dispatch(clearUserKnowledgeTest())
    }
  }, [userSuitabilityTest])

  // ? Manage User KYC
  useEffect(() => {
    dispatch(setUserKycSubLifeCycleStatus(userKyc.subLifeCycleStatus))
    if (userKyc?.data && userKyc.subLifeCycleStatus === 'live') {
      dispatch(setUserKyc(userKyc.data))
    } else if (userVerificationDetails.data === undefined) {
      dispatch(clearUserKyc())
    }
  }, [userKyc])

  return null
}

export default SupabaseUpdater
